import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import UploadFile from 'components/Form/UploadFile';
import ModalAnaliseJulia from 'components/Modal/analisejulia';
import ModalCuradoria from 'components/Modal/ModalTornarElegivel';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { CreateFatura, Read } from 'services/financing-proposals';
import { Icon, Load, StatusContent, Title } from 'ui/styled';
import {
  exposeStrapiError,
  marcadorDiferente,
  parseStrapiImage,
  selectColors,
} from 'utils';
import {
  formatDocuments,
  formatElapsedTime,
  parseCurrency,
} from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

// Componentes Styled ou Auxiliares Locais
const Touch = ({ onClick, children }) => (
  <div onClick={onClick} style={{ cursor: 'pointer' }}>
    {children}
  </div>
);
const TableInfo = ({ children }) => (
  <span style={{ color: 'blue' }}>{children}</span>
);
const TableInfoPend = ({ children }) => (
  <span style={{ color: 'red', cursor: 'pointer' }}>{children}</span>
);

export default function CuradoriaProposal() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, reloadContent, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0); // Para forçar re-renderização de tempo decorrido
  const [isModalOpen, setIsModalOpen] = useState(false); // ModalAnaliseJulia
  const [selectedProposal, setSelectedProposal] = useState(null); // Dados para ModalAnaliseJulia
  const [showModal, setShowModal] = useState(false); // ModalCuradoria
  const [modalData, setModalData] = useState(null); // Dados para ModalCuradoria
  const [loadingFatura, setLoadingFatura] = useState(() => {
    const storedLoading = sessionStorage.getItem('financingDeskLoadingFatura');
    try {
      const parsed = storedLoading ? JSON.parse(storedLoading) : {};
      return Object.keys(parsed).filter((id) => parsed[id] === 'anexando');
    } catch (e) {
      console.error('Erro ao parsear financingDeskLoadingFatura:', e);
      return [];
    }
  });
  // ---------------------------------------------------------------------------
  // FUNÇÕES DE UTILIDADE E ARMAZENAMENTO LOCAL
  // ---------------------------------------------------------------------------
  /** Gerencia o estado de loading de fatura no sessionStorage. */
  const updateStorage = (propostaId, status) => {
    let storageData =
      JSON.parse(sessionStorage.getItem('financingDeskLoadingFatura')) || {};
    if (status === false) {
      delete storageData[propostaId];
    } else {
      storageData[propostaId] = status;
    }
    sessionStorage.setItem(
      'financingDeskLoadingFatura',
      JSON.stringify(storageData)
    );
    setLoadingFatura(
      Object.keys(storageData).filter((id) => storageData[id] === 'anexando')
    );
  };
  /** Lê o estado de loading de fatura do sessionStorage. */
  const readStorage = (propostaId) => {
    const storageData =
      JSON.parse(sessionStorage.getItem('financingDeskLoadingFatura')) || {};
    return storageData[propostaId] || null;
  };
  // ---------------------------------------------------------------------------
  // HANDLERS E FUNÇÕES DE SERVIÇO (CRUD/API)
  // ---------------------------------------------------------------------------
  /** Inicializa e carrega os dados das propostas. */
  const init = useCallback(async () => {
    setLoading(true);
    try {
      const populateQuery = [
        'julia_fatura',
        'espelho_pdf',
        'distribuidor',
        'integrador',
        'informacoes_cliente',
        'informacoes_projeto',
        'endereco_instalacao',
      ]
        .map((p, i) => `populate[${i}]=${p}`)
        .join('&');
      const result = await Read(
        `?filters[status]=recusado&filters[curadoria_analise]=Sim&${populateQuery}`
      );
      if (result && !exposeStrapiError(result)) {
        const sorted = result?.results?.sort((a, b) => b.id - a.id);
        const next = sorted?.map((m) => ({
          ...m,
          accepted: m?.propostas_banco?.find((f) => f.aceito),
        }));
        setRegisters(next);
        // Limpar estados obsoletos no sessionStorage
        const storageData =
          JSON.parse(sessionStorage.getItem('financingDeskLoadingFatura')) ||
          {};
        const validIds = next.map((item) => item.id.toString());
        Object.keys(storageData).forEach((key) => {
          if (!validIds.includes(key)) {
            delete storageData[key];
          }
        });
        sessionStorage.setItem(
          'financingDeskLoadingFatura',
          JSON.stringify(storageData)
        );
        setLoadingFatura(
          Object.keys(storageData).filter(
            (id) => storageData[id] === 'anexando'
          )
        );
        // Configura opções de filtro
      } else {
        toast.error('Erro ao carregar dados.');
      }
    } catch (error) {
      console.error('Erro ao inicializar dados:', error);
      toast.error('Erro ao carregar propostas.');
    } finally {
      setLoading(false);
    }
  }, []);
  /** Envia a fatura para a API. */
  const saveFatura = useCallback(
    async ({ fatura, proposta, cliente }) => {
      const propostaId = proposta?.toString();
      if (!propostaId) {
        toast.error('ID da proposta inválido.');
        return;
      }
      try {
        updateStorage(propostaId, 'anexando');
        const dadoTratado = {
          cliente,
          fatura: fatura.id,
          proposta: propostaId,
        };
        toast.info('Fatura enviada, aguarde o processamento!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
        const ret = await CreateFatura({ data: dadoTratado });
        if (ret && !exposeStrapiError(ret)) {
          if (ret.resposta_ocr) {
            let errorMsg = null;
            if (ret?.resposta_ocr?.faturaenergia === 'nao') {
              errorMsg =
                'A imagem anexada não corresponde a uma fatura de energia!';
            } else if (ret.resposta_ocr.resolucao === 'ruim') {
              errorMsg =
                'Infelizmente a qualidade da imagem não atende nossos padrões! Por favor, anexe uma imagem de melhor qualidade.';
            } else if (ret.resposta_ocr.leituraAtual === 'nao') {
              errorMsg =
                'A data de leitura da fatura tem mais de 60 dias. Precisamos de uma fatura mais recente.';
            }
            if (errorMsg) {
              throw new Error(errorMsg);
            }
          }
          toast.success('Fatura anexada com sucesso!');
        } else {
          throw new Error('Erro ao anexar fatura.');
        }
      } catch (error) {
        const erroTratado = error.message || 'Tente novamente.';
        toast.error('Ocorreu um erro ao enviar a fatura. ' + erroTratado, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
        });
      } finally {
        updateStorage(propostaId, false);
        setLoadingFatura((prev) => prev.filter((id) => id !== propostaId));
        await init();
        reloadContent(true);
        setTick((prev) => prev + 1);
      }
    },
    [init, reloadContent]
  );
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProposal(null);
  };
  // ---------------------------------------------------------------------------
  // EFEITOS (USEEFFECT)
  // ---------------------------------------------------------------------------
  // Efeito para carregar dados na montagem
  useEffect(() => init(), [tick, init]);
  // Efeito para atualizar o tempo decorrido
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 60000); // 1 minuto
    return () => clearInterval(interval);
  }, []);
  // ---------------------------------------------------------------------------
  // DADOS DERIVADOS (USEMEMO)
  // ---------------------------------------------------------------------------
  /** Definição das colunas da tabela. */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        ref: 'id',
        renderCell: ({ row }) => {
          //Verifica se foi analisado pela curadoria humana ou pelo bot
          const isAnalisadoCuradoria = row?.curadoria_analise === 'Analisado';
          const isAnalisadoBot = row?.curadoria_analise_bot === 'Analisado';
          const id = row?.id;
          if (isAnalisadoCuradoria || isAnalisadoBot) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        ref: 'date',
        sortable: true,
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'SLA',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
    ];
    const distributorColumn = { title: 'Distribuidora', ref: 'distributor' };
    const integratorClientColumns = [
      { title: 'Integrador', ref: 'integrator' },
      { title: 'Cliente', ref: 'customer' },
    ];
    const valueColumn = { title: 'Valor (R$)', ref: 'value' };
    const faturaColumn = {
      title: 'Fatura',
      ref: 'fatura',
      renderCell: ({ row }) => {
        const isAnalyzing =
          loadingFatura.includes(row.id.toString()) ||
          readStorage(row.id.toString()) === 'anexando';
        const hasFatura = !!row?.url_fatura;
        const cellStyle = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '30px',
          width: '20px',
          margin: '0 auto',
        };
        const uploadFileStyle = {
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '80%',
          maxWidth: '80px',
          cursor: 'pointer',
        };
        return (
          <div style={cellStyle}>
            {isAnalyzing && !hasFatura ? (
              <Load />
            ) : hasFatura ? (
              <Touch
                onClick={() =>
                  window.open(parseStrapiImage(row?.url_fatura), 'new')
                }
              >
                <TableInfo>Anexado</TableInfo>
              </Touch>
            ) : (
              <div style={uploadFileStyle}>
                <UploadFile
                  onChange={(file) =>
                    saveFatura({
                      fatura: file,
                      proposta: row.id,
                      cliente: row?.customer,
                    })
                  }
                  proposalType="Elegiveis"
                >
                  <TableInfoPend>Anexar</TableInfoPend>
                </UploadFile>
              </div>
            )}
          </div>
        );
      },
    };
    const tagColumn = {
      title: 'TAG',
      renderCell: ({ row }) => {
        const isAnalyzing =
          loadingFatura.includes(row.id.toString()) ||
          readStorage(row.id.toString()) === 'anexando';
        const hasFatura = !!row?.url_fatura;
        let currentTag;
        let tagColor;
        if (isAnalyzing && !hasFatura) {
          currentTag = 'Verificando';
          tagColor = 'Verificacao';
        } else if (hasFatura) {
          currentTag = 'Em análise';
          tagColor = 'Analise';
        } else {
          currentTag = 'Anexar Fatura';
          tagColor = 'Pendente';
        }
        return (
          <StatusContent color={selectColors(tagColor)}>
            {currentTag}
          </StatusContent>
        );
      },
    };
    const espelhoColumn = user?.administrador
      ? {
          title: 'Espelho da Proposta',
          renderCell: ({ row }) => (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={() => navigate(`proposals/financing/${row?.id}`)}
                color="blue"
                nospace
                mbutton
                small
              >
                Visualizar
              </Button>
            </div>
          ),
        }
      : null;
    const navigateColumn = {
      title: '',
      renderCell: ({ row }) => (
        <Icon
          icon="arrow-right"
          pointer
          onClick={() =>
            navigate(`menu_adm/financingtablecuradoria/${row?.id}`)
          }
        />
      ),
    };
    let finalColumns = [...baseColumns];
    const hasMultipleDistributors =
      user?.distribuidores && user.distribuidores.length >= 1;
    if (user?.administrador || hasMultipleDistributors) {
      finalColumns.push(distributorColumn);
    }
    finalColumns.push(...integratorClientColumns);
    finalColumns.push(valueColumn);
    finalColumns.push(faturaColumn);
    finalColumns.push(tagColumn);
    if (espelhoColumn) finalColumns.push(espelhoColumn);
    // if (juliaColumn) finalColumns.push(juliaColumn);
    // finalColumns.push(tornarElegivelColumn);
    finalColumns.push(navigateColumn);
    return finalColumns;
  }, [user, loadingFatura, navigate, saveFatura]); // Dependências: user, loadingFatura, navigate
  /** Mapeamento dos registros para as linhas da tabela. */
  const rows = useMemo(() => {
    return registers?.map((item) => ({
      id: item?.id,
      date: item?.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(item?.createdAt),
      cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
      distributor: normalizeName(item?.distribuidor?.trade_name),
      integrator: normalizeName(item?.integrador?.nome_fantasia),
      customer: normalizeName(item?.informacoes_cliente?.nome_completo),
      value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
      status: normalizeName(item?.status),
      espelho_pdf:
        item?.espelho_pdf?.[0]?.url ??
        item?.espelho_pdf?.data?.[0]?.attributes?.url ??
        null,
      url_fatura: item?.url_fatura,
      julia_fatura: item?.julia_fatura,
      endereco_instalacao: item?.endereco_instalacao,
      tag: item?.url_fatura ? 'Em analise' : 'Anexar Fatura',
      curadoria_analise: item?.curadoria_analise, // Assumindo que este campo está no backend
      curadoria_analise_bot: item?.curadoria_analise_bot,
      cnpj_integrador: formatDocuments(item?.integrador?.cnpj),
      cnpj_integrador_clean: item?.integrador?.cnpj.replace(/\D/g, ''),
      cpf_cnpj: item?.cliente?.cpf || item?.cliente?.cnpj,
      cpf_cnpj_clean: item?.cliente?.cpf
        ? item?.cliente?.cpf.replace(/\D/g, '')
        : item?.cliente?.cnpj.replace(/\D/g, ''),
    }));
  }, [registers]);

  return (
    <ContainerAuthenticated>
      <Title>Curadoria</Title>
      <TableWrapper title="Propostas" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
      {/* Modal Tornar Elegível (Curadoria) */}
      {showModal && modalData && (
        <ModalCuradoria
          id={modalData.id}
          reload={modalData.reload}
          onClose={() => {
            setShowModal(false);
            setModalData(null);
          }}
          newStatus="elegivel"
          confirmationText="transformar essa Proposta em Elegível"
        />
      )}
      {/* Modal Análise da Júlia */}
      {isModalOpen && selectedProposal && (
        <ModalAnaliseJulia
          julia_fatura={selectedProposal}
          onClose={closeModal}
        />
      )}
    </ContainerAuthenticated>
  );
}
